/* Copyright (c) 2020-present CloudIO Inc */

import { getManager } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { CloudDataStoreItem, defaultValue } from './CloudDataStore';
import {
  controlledBy,
  displayName,
  dsAppUidProp,
  getNextAlias,
  hideDirtyIndicator,
} from '../DataStore/DataStoreManager';
import { camelCase } from 'lodash-es';
import { emptyFunction } from '@cloudio-saas/statex';

const CloudDataStoreManager: ComponentManagerFn<'CloudDataStore'> = (
  platform: Platform,
) => {
  let timer: NodeJS.Timeout;
  let callback = emptyFunction;
  return {
    ...baseComponentManager<'CloudDataStore'>(platform, 'CloudDataStore'),
    type: 'CloudDataStore',
    name: 'Cloud Data Store',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerAfterLoad',
          'TriggerAfterSave',
          'TriggerBeforeCreate',
          'TriggerBeforeLoad',
          'TriggerBeforeSave',
          'TriggerOnInitialize',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Cloud Function',
        type: 'FunctionURIChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
        getAppUid(page, item, context) {
          return (item as CloudDataStoreItem).props.dsAppUid;
        },
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      jsonData: {
        label: 'JSON Data',
        type: 'json',
        required: true,
      },
      insertable: {
        label: 'Insertable',
        type: 'booleanExpression',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
      },
      deletable: {
        label: 'Deletable',
        type: 'boolean',
      },
      autoQuery: {
        label: 'Auto Query',
        type: 'boolean',
      },
      filterLocally: {
        label: 'Filter Locally',
        type: 'boolean',
      },
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      cacheAll: {
        label: 'Cache All',
        type: 'boolean',
        helpText:
          'Check this to cache query result. With cache enabled, duplicate queries are not sent to the server.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      controlledBy,
      hideDirtyIndicator,
    },
    isPropertyVisible: (property, item) => {
      switch (property) {
        case 'dsAppUid':
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        case 'jsonData':
          return !!item.alias;
        default:
          return !!item.alias;
      }
    },
    onPropertyChange: (property, value, itemProps, { appUid, pageId }) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
          dataSource: '',
        };
      } else if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      return {
        [property]: value,
      };
    },
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' || type === 'QUERY_FILTER'
        ? { aliasProp: 'alias' }
        : undefined,
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    generateShortId: (item) => item.alias,
    replaceableWith: ['LocalDataStore'],
    category: 'Data',
    icon: { iconName: 'database' },
    description: `Use a Cloud Data Store if your data is backed by a Cloud Function without any dependency on any Data Source backend.

This is useful to interact with simple REST API that doesn't require the power and complexities of External Data Source.
`,
  };
};

export default CloudDataStoreManager;
